// GAVION: a manual note on a compagnie (lead page « Ajouter une note », tasks page « Noter »).
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useCreate, useGetIdentity, useNotify, type Identifier } from "ra-core";

/**
 * save(text, onSaved): an empty note is refused with a warning; on success « Note ajoutée », the timeline is
 * refreshed and onSaved runs. On error the caller keeps its dialog and text so the rep can retry.
 */
export const useAddCompanyNote = (companyId: Identifier) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const [create, { isPending }] = useCreate();
  const save = useCallback(
    (text: string, onSaved?: () => void) => {
      const body = text.trim();
      if (!body) {
        notify("gavion.lead.note.empty", { type: "warning" });
        return;
      }
      create(
        "company_notes",
        {
          data: {
            company_id: companyId,
            text: body,
            // FakeRest (demo/tests) has no column defaults, and v_timeline drops rows without a date.
            date: new Date().toISOString(),
            sales_id: identity?.id ?? null,
            source: "manual",
          },
        },
        {
          onSuccess: () => {
            notify("gavion.lead.note.saved", { type: "success" });
            queryClient.invalidateQueries({ queryKey: ["v_timeline"] });
            onSaved?.();
          },
          onError: () =>
            notify("ra.notification.http_error", { type: "error" }),
        },
      );
    },
    [companyId, create, identity?.id, notify, queryClient],
  );
  return { save, isPending };
};
